import { EnrollmentFlow, Loading } from "@uzverse/ui";
import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import type { EnrollmentResult, InviteInfo, TotpEnrollment } from "../api/types.js";
import { ErrorNote, useCommonT, useErrorText } from "../components/common.js";
import { AuthHeader } from "../components/Layout.js";
import { useSession } from "../state/session.js";

export function InvitePage() {
  const { t } = useTranslation("web");
  const tc = useCommonT();
  const errorText = useErrorText();
  const navigate = useNavigate();
  const { inviteToken = "" } = useParams();
  const { services, signIn } = useSession();
  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [invalid, setInvalid] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [disclosure, setDisclosure] = useState(false);
  const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The fresh session is held back until the user confirms they saved the recovery codes.
  const confirmed = useRef<EnrollmentResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    services.api
      .getInvite(inviteToken)
      .then((info) => !cancelled && setInvite(info))
      .catch(() => !cancelled && setInvalid(true));
    return () => {
      cancelled = true;
    };
  }, [services, inviteToken]);

  function accept(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    services.api
      .acceptInvite({
        inviteToken,
        displayName: displayName.trim(),
        password,
        disclosureAccepted: disclosure,
      })
      .then(setEnrollment)
      .catch((err: unknown) => setError(errorText(err)))
      .finally(() => setBusy(false));
  }

  return (
    <main className="auth">
      <div className="auth-card card">
        <AuthHeader />
        <h1>{enrollment ? tc("ui.enroll.title") : t("invite.title")}</h1>
        {invalid ? (
          <p role="alert" className="note-error">
            {t("invite.invalid")}
          </p>
        ) : !invite ? (
          <Loading t={tc} />
        ) : enrollment ? (
          <EnrollmentFlow
            t={tc}
            seed={{ otpauthUri: enrollment.otpauthUri, secret: enrollment.manualKey }}
            errorText={errorText}
            confirm={async (code) => {
              const done = await services.api.confirmTotpEnrollment(enrollment.enrollmentId, code);
              confirmed.current = done;
              // The seed is not needed any more; drop it from state as soon as it is confirmed.
              return done.recoveryCodes;
            }}
            onFinished={() => {
              if (!confirmed.current) return;
              signIn(confirmed.current);
              confirmed.current = null;
              void navigate("/welcome", { replace: true });
            }}
          />
        ) : (
          <form onSubmit={accept} className="stack">
            <p>{t("invite.intro", { name: invite.invitedByName, email: invite.email })}</p>
            <label className="field">
              <span>{t("invite.displayName")}</span>
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                autoComplete="name"
                required
              />
            </label>
            <label className="field">
              <span>{t("invite.password")}</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                minLength={12}
                required
                aria-describedby="pw-hint"
              />
              <small id="pw-hint" className="muted">
                {t("invite.passwordHint")}
              </small>
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={disclosure}
                onChange={(e) => setDisclosure(e.target.checked)}
                required
              />
              <span>{t("invite.disclosure")}</span>
            </label>
            <ErrorNote message={error} />
            <button type="submit" className="btn btn-primary" disabled={busy || !disclosure}>
              {t("invite.accept")}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
